/**
 * AdminJournalPage — founder-only tracker for outreach posts + field events.
 *
 * Purpose: timestamped portfolio of receipts. Every FB / Reddit / Rachio
 * Community drop and every storm-skip / application gets logged here so you
 * can build evidence over time. Goal is *your* memory, not a customer-facing
 * feature — admin-gated end to end.
 *
 * Two tabs:
 *   1. Outreach Posts — where, when, what you posted, status
 *   2. Field Events   — what the lawn did (storm skip, app log, fungal note)
 *                       with optional zone-by-zone before/after readings.
 *                       "Capture Ecowitt now" pre-fills the "after" column.
 *
 * Backend: /api/admin/journal/* (see backend/routes/journal.py)
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Download, Trash2, Camera, Plus, FileText, Sprout, Paperclip, X } from 'lucide-react';
import { API_URL } from '../config/api';

const PLATFORMS = [
  { value: 'facebook', label: 'Facebook' },
  { value: 'reddit', label: 'Reddit' },
  { value: 'rachio-community', label: 'Rachio Community' },
  { value: 'home-assistant', label: 'Home Assistant Forum' },
  { value: 'aqara', label: 'Aqara Forum' },
  { value: 'x', label: 'X / Twitter' },
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'other', label: 'Other' },
];

const EVENT_TYPES = [
  { value: 'storm-skip', label: 'Storm skip' },
  { value: 'application', label: 'Application logged' },
  { value: 'fungal-pressure', label: 'Fungal pressure note' },
  { value: 'mowing', label: 'Mowing' },
  { value: 'observation', label: 'Field observation' },
  { value: 'other', label: 'Other' },
];

const STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'posted', label: 'Posted' },
  { value: 'replied', label: 'Engaged' },
  { value: 'archived', label: 'Archived' },
];

const STATUS_COLORS = {
  draft: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-700 dark:text-zinc-200',
  posted: 'bg-emerald-200 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200',
  replied: 'bg-amber-200 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200',
  archived: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-800 dark:text-zinc-500',
};

// ──────────────────────────────────────────────────────────────────────────
// Attachment uploader — shared between Outreach + Field Events.
// Multi-select, mobile-friendly (accept includes camera capture on iOS).
// `value` is an array of {id, url, filename, content_type} attachment dicts,
// `onChange` returns the same shape. Upload happens immediately on file pick
// so the form already has IDs by the time the user hits Save.
// ──────────────────────────────────────────────────────────────────────────
const AttachmentUploader = ({ value, onChange, testIdPrefix }) => {
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const handlePick = () => inputRef.current?.click();

  const handleChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    // Soft client-side guard so users don't waste bandwidth on huge files
    const tooBig = files.find((f) => f.size > 12 * 1024 * 1024);
    if (tooBig) {
      toast.error(`"${tooBig.name}" is over 12MB. Compress it first.`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append('files', f));
      const res = await axios.post(`${API_URL}/api/admin/journal/upload`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const added = res.data?.attachments || [];
      onChange([...(value || []), ...added]);
      toast.success(`Attached ${added.length} screenshot${added.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = (id) => {
    onChange((value || []).filter((a) => a.id !== id));
  };

  return (
    <div data-testid={`${testIdPrefix}-uploader`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        capture="environment"
        className="hidden"
        onChange={handleChange}
        data-testid={`${testIdPrefix}-file-input`}
      />
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs">Screenshots ({(value || []).length})</Label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handlePick}
          disabled={uploading}
          data-testid={`${testIdPrefix}-pick`}
        >
          <Paperclip className="w-3 h-3 mr-1" />
          {uploading ? 'Uploading…' : 'Add screenshots'}
        </Button>
      </div>
      {(value || []).length === 0 ? (
        <p className="text-xs text-muted-foreground italic">
          Tap to attach FB comment caps, Ecowitt charts, Rachio logs — anything that proves the receipt.
        </p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {value.map((a) => (
            <div
              key={a.id}
              className="relative group rounded-md overflow-hidden border border-border bg-muted/30 aspect-square"
              data-testid={`${testIdPrefix}-thumb-${a.id}`}
            >
              <img
                src={`${API_URL}${a.url}`}
                alt={a.filename || ''}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <button
                type="button"
                onClick={() => handleRemove(a.id)}
                className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove"
                data-testid={`${testIdPrefix}-remove-${a.id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Read-side gallery — embeds in each journal item card.
const AttachmentGallery = ({ attachments, testIdPrefix }) => {
  const [lightbox, setLightbox] = useState(null);
  if (!attachments || attachments.length === 0) return null;
  return (
    <>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 mt-2" data-testid={`${testIdPrefix}-gallery`}>
        {attachments.map((a) => (
          <button
            key={a.id}
            type="button"
            onClick={() => setLightbox(a)}
            className="block rounded-md overflow-hidden border border-border bg-muted/30 aspect-square hover:ring-2 hover:ring-emerald-500 transition-all"
            data-testid={`${testIdPrefix}-attach-${a.id}`}
          >
            <img
              src={`${API_URL}${a.url}`}
              alt={a.filename || ''}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </button>
        ))}
      </div>
      {lightbox && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="attachment-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            aria-label="Close"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={`${API_URL}${lightbox.url}`}
            alt={lightbox.filename || ''}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-md shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default function AdminJournalPage() {
  return (
    <div className="min-h-screen bg-background" data-testid="admin-journal-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="ghost" size="sm" data-testid="journal-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Admin
              </Button>
            </Link>
            <div>
              <h1 className="text-xl font-black tracking-tight">Founder Journal</h1>
              <p className="text-xs text-muted-foreground">
                Outreach posts + field events. Build receipts over time.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="outreach" className="w-full">
          <TabsList className="mb-6" data-testid="journal-tabs">
            <TabsTrigger value="outreach" data-testid="tab-outreach">
              <FileText className="w-4 h-4 mr-1.5" /> Outreach Posts
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events">
              <Sprout className="w-4 h-4 mr-1.5" /> Field Events
            </TabsTrigger>
          </TabsList>

          <TabsContent value="outreach">
            <OutreachTab />
          </TabsContent>

          <TabsContent value="events">
            <FieldEventsTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Outreach tab
// ──────────────────────────────────────────────────────────────────────────
const OutreachTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    platform: 'facebook',
    url: '',
    snippet: '',
    status: 'posted',
    notes: '',
    posted_at: '',
    attachments: [],
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/journal/outreach`, { withCredentials: true });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load outreach log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.snippet.trim()) {
      toast.error('Snippet is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/journal/outreach`, {
        platform: form.platform,
        url: form.url || null,
        snippet: form.snippet,
        status: form.status,
        notes: form.notes || null,
        posted_at: form.posted_at || null,
        attachment_ids: (form.attachments || []).map((a) => a.id),
      }, { withCredentials: true });
      toast.success('Logged');
      setForm({ platform: form.platform, url: '', snippet: '', status: 'posted', notes: '', posted_at: '', attachments: [] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/journal/outreach/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <Card data-testid="outreach-form-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="w-4 h-4" /> Log a post
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <Label className="text-xs">Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger data-testid="outreach-platform"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">URL (optional)</Label>
                <Input
                  type="url"
                  placeholder="https://..."
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  data-testid="outreach-url"
                />
              </div>
              <div>
                <Label className="text-xs">What you posted *</Label>
                <Textarea
                  rows={5}
                  placeholder="Paste the actual post copy so you can search & reuse it later..."
                  value={form.snippet}
                  onChange={(e) => setForm({ ...form, snippet: e.target.value })}
                  required
                  data-testid="outreach-snippet"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="outreach-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Posted date</Label>
                  <Input
                    type="datetime-local"
                    value={form.posted_at}
                    onChange={(e) => setForm({ ...form, posted_at: e.target.value })}
                    data-testid="outreach-posted-at"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Notes (optional)</Label>
                <Textarea
                  rows={2}
                  placeholder="Engagement, replies, anything to remember..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="outreach-notes"
                />
              </div>
              <div className="rounded-md border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 p-3">
                <AttachmentUploader
                  value={form.attachments}
                  onChange={(next) => setForm({ ...form, attachments: next })}
                  testIdPrefix="outreach-attach"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="outreach-save">
                Save entry
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Recent posts ({items.length})</CardTitle>
            <a
              href={`${API_URL}/api/admin/journal/outreach.csv`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="outreach-export-csv"
            >
              <Button variant="outline" size="sm">
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
            </a>
          </CardHeader>
          <CardContent>
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && items.length === 0 && (
              <div className="rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 px-4 py-8 text-center text-sm text-muted-foreground" data-testid="outreach-empty">
                No outreach logged yet. Log your first FB post or Rachio Community reply on the left.
              </div>
            )}
            <div className="space-y-3">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="rounded-md border border-border bg-card/50 p-3"
                  data-testid={`outreach-row-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                        {it.platform}
                      </Badge>
                      <Badge className={`text-[10px] ${STATUS_COLORS[it.status] || ''}`}>
                        {it.status}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground">
                        {it.posted_at && new Date(it.posted_at).toLocaleString()}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(it.id)}
                      className="text-zinc-400 hover:text-red-500 shrink-0"
                      data-testid={`outreach-delete-${it.id}`}
                      aria-label="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {it.url && (
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline break-all block mb-1"
                    >
                      {it.url}
                    </a>
                  )}
                  <p className="text-sm whitespace-pre-wrap text-zinc-800 dark:text-zinc-200 leading-snug">
                    {it.snippet}
                  </p>
                  {it.notes && (
                    <p className="text-xs text-muted-foreground italic mt-2 border-t border-border pt-2">
                      {it.notes}
                    </p>
                  )}
                  <AttachmentGallery attachments={it.attachments} testIdPrefix={`outreach-${it.id}`} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Field events tab
// ──────────────────────────────────────────────────────────────────────────
const FieldEventsTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapping, setSnapping] = useState(false);
  const [form, setForm] = useState({
    event_type: 'storm-skip',
    occurred_at: '',
    notes: '',
    readings: [],
    screenshot_url: '',
    attachments: [],
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/admin/journal/events`, { withCredentials: true });
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSnapshot = async () => {
    try {
      setSnapping(true);
      const res = await axios.get(`${API_URL}/api/admin/journal/ecowitt-snapshot`, { withCredentials: true });
      const channels = res.data?.channels || [];
      if (channels.length === 0) {
        toast.error('No soil channels reporting right now');
        return;
      }
      // Fill the "after" column for each channel. Preserve existing "before" if user already entered one.
      const merged = channels.map((c) => {
        const existing = form.readings.find((r) => r.zone === c.zone) || {};
        return {
          zone: c.zone,
          label: existing.label || c.zone,
          before: existing.before ?? null,
          after: c.moisture_pct,
          temp_f: c.temp_f,
          unit: '%',
        };
      });
      setForm({ ...form, readings: merged });
      toast.success(`Captured ${channels.length} channel${channels.length === 1 ? '' : 's'}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Snapshot failed');
    } finally {
      setSnapping(false);
    }
  };

  const handleReadingChange = (zone, field, value) => {
    setForm({
      ...form,
      readings: form.readings.map((r) =>
        r.zone === zone ? { ...r, [field]: value === '' ? null : Number(value) } : r,
      ),
    });
  };

  const handleAddReading = () => {
    const nextNum = form.readings.length + 1;
    setForm({
      ...form,
      readings: [...form.readings, { zone: `CH${nextNum}`, label: `Zone ${nextNum}`, before: null, after: null, unit: '%' }],
    });
  };

  const handleRemoveReading = (zone) => {
    setForm({ ...form, readings: form.readings.filter((r) => r.zone !== zone) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/journal/events`, {
        event_type: form.event_type,
        occurred_at: form.occurred_at || null,
        notes: form.notes || null,
        readings: form.readings,
        screenshot_url: form.screenshot_url || null,
        attachment_ids: (form.attachments || []).map((a) => a.id),
      }, { withCredentials: true });
      toast.success('Event logged');
      setForm({ event_type: form.event_type, occurred_at: '', notes: '', readings: [], screenshot_url: '', attachments: [] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/journal/events/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6">
      <div className="lg:col-span-2">
        <Card data-testid="events-form-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Plus className="w-4 h-4" /> Log a field event
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                    <SelectTrigger data-testid="event-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">When</Label>
                  <Input
                    type="datetime-local"
                    value={form.occurred_at}
                    onChange={(e) => setForm({ ...form, occurred_at: e.target.value })}
                    data-testid="event-occurred-at"
                  />
                </div>
              </div>

              <div className="rounded-md border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-bold text-emerald-900 dark:text-emerald-200">
                    Soil readings ({form.readings.length})
                  </Label>
                  <div className="flex gap-1.5">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={handleAddReading}
                      data-testid="event-add-reading"
                    >
                      <Plus className="w-3 h-3" /> Row
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSnapshot}
                      disabled={snapping}
                      data-testid="event-capture-snapshot"
                    >
                      <Camera className="w-3 h-3 mr-1" />
                      {snapping ? 'Capturing…' : 'Capture Ecowitt now'}
                    </Button>
                  </div>
                </div>
                {form.readings.length === 0 && (
                  <p className="text-xs text-emerald-800/70 dark:text-emerald-200/60 italic">
                    Optional. Click "Capture Ecowitt now" to pull live readings,
                    or "+ Row" to add manual entries.
                  </p>
                )}
                <div className="space-y-1.5">
                  {form.readings.map((r) => (
                    <div
                      key={r.zone}
                      className="grid grid-cols-12 gap-1.5 items-center text-xs"
                      data-testid={`event-reading-${r.zone}`}
                    >
                      <Input
                        className="col-span-3 h-8 text-xs"
                        value={r.label}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            readings: form.readings.map((rr) => rr.zone === r.zone ? { ...rr, label: e.target.value } : rr),
                          })
                        }
                        placeholder="Label"
                      />
                      <Input
                        className="col-span-4 h-8 text-xs"
                        type="number"
                        step="0.1"
                        value={r.before ?? ''}
                        onChange={(e) => handleReadingChange(r.zone, 'before', e.target.value)}
                        placeholder="Before %"
                      />
                      <Input
                        className="col-span-4 h-8 text-xs"
                        type="number"
                        step="0.1"
                        value={r.after ?? ''}
                        onChange={(e) => handleReadingChange(r.zone, 'after', e.target.value)}
                        placeholder="After %"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveReading(r.zone)}
                        className="col-span-1 text-zinc-400 hover:text-red-500"
                        aria-label="Remove"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">Notes</Label>
                <Textarea
                  rows={3}
                  placeholder="What happened, why it matters, recovery plan..."
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="event-notes"
                />
              </div>

              <div>
                <Label className="text-xs">Screenshot URL (optional)</Label>
                <Input
                  placeholder="https://... (Ecowitt screenshot, Rachio screenshot)"
                  value={form.screenshot_url}
                  onChange={(e) => setForm({ ...form, screenshot_url: e.target.value })}
                  data-testid="event-screenshot"
                />
              </div>

              <div className="rounded-md border border-emerald-300 dark:border-emerald-800 bg-emerald-50/60 dark:bg-emerald-950/30 p-3">
                <AttachmentUploader
                  value={form.attachments}
                  onChange={(next) => setForm({ ...form, attachments: next })}
                  testIdPrefix="event-attach"
                />
              </div>

              <Button type="submit" className="w-full" data-testid="event-save">
                Save event
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <div className="lg:col-span-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">Recent events ({items.length})</CardTitle>
            <a
              href={`${API_URL}/api/admin/journal/events.csv`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="events-export-csv"
            >
              <Button variant="outline" size="sm">
                <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
              </Button>
            </a>
          </CardHeader>
          <CardContent>
            {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!loading && items.length === 0 && (
              <div className="rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 px-4 py-8 text-center text-sm text-muted-foreground" data-testid="events-empty">
                No field events yet. Log the May 9 thunderstorm skip as your first
                entry — Ecowitt snapshot will pre-fill your "after" readings.
              </div>
            )}
            <div className="space-y-3">
              {items.map((it) => (
                <div
                  key={it.id}
                  className="rounded-md border border-border bg-card/50 p-3"
                  data-testid={`event-row-${it.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                        {it.event_type?.replace('-', ' ')}
                      </Badge>
                      <span className="text-[11px] text-muted-foreground">
                        {it.occurred_at && new Date(it.occurred_at).toLocaleString()}
                      </span>
                    </div>
                    <button
                      onClick={() => handleDelete(it.id)}
                      className="text-zinc-400 hover:text-red-500 shrink-0"
                      aria-label="Delete"
                      data-testid={`event-delete-${it.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {it.readings && it.readings.length > 0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 my-2">
                      {it.readings.map((r) => {
                        const delta = (r.before !== null && r.after !== null) ? (Number(r.after) - Number(r.before)) : null;
                        return (
                          <div
                            key={r.zone}
                            className="rounded border border-zinc-200 dark:border-zinc-800 bg-white/60 dark:bg-zinc-900/60 px-2 py-1 text-[11px]"
                          >
                            <div className="font-bold text-zinc-900 dark:text-zinc-50 truncate">{r.label || r.zone}</div>
                            <div className="text-zinc-600 dark:text-zinc-400">
                              {r.before ?? '–'} → <strong>{r.after ?? '–'}</strong>{r.unit || '%'}
                              {delta !== null && (
                                <span className={`ml-1.5 font-bold ${delta > 0 ? 'text-emerald-600' : 'text-amber-600'}`}>
                                  {delta > 0 ? '+' : ''}{delta.toFixed(1)}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {it.notes && (
                    <p className="text-sm whitespace-pre-wrap text-zinc-800 dark:text-zinc-200 leading-snug mt-1">
                      {it.notes}
                    </p>
                  )}
                  {it.screenshot_url && (
                    <a
                      href={it.screenshot_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-700 dark:text-emerald-400 hover:underline break-all block mt-2"
                    >
                      📷 {it.screenshot_url}
                    </a>
                  )}
                  <AttachmentGallery attachments={it.attachments} testIdPrefix={`event-${it.id}`} />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
